// Map styles (base maps), the water depth overlay, and catch markers.
import type { Catch, LatLon } from "../../shared/model.ts";
import type { TextKey } from "../../shared/strings.ts";
import { MML_API_KEY } from "../config.ts";
import { el, icon } from "../dom.ts";
import { catches } from "../state.ts";
import { lsGet, lsSet } from "../util.ts";

/** Leaflet comes from a CDN <script>; on a first visit without signal it may be missing. */
export const hasLeaflet = (): boolean => typeof L !== "undefined";

export const FINLAND: LatLon = [62.5, 25.8];

export type LayerName = "terrain" | "mml" | "streets";
interface BaseLayer {
  url: string;
  /** Deepest zoom the map can show. */
  max: number;
  /** Deepest zoom the server has tiles for (deeper zooms are scaled up). */
  native?: number;
  attr: string;
  name: TextKey;
  desc: TextKey;
}
export const LAYERS: Partial<Record<LayerName, BaseLayer>> = {
  terrain: { url: "https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png", max: 17, attr: "© OpenStreetMap contributors, SRTM | © OpenTopoMap (CC-BY-SA)", name: "mapTerrain", desc: "mapTerrainDesc" },
  streets: { url: "https://tile.openstreetmap.org/{z}/{x}/{y}.png", max: 19, attr: "© OpenStreetMap contributors", name: "mapStreets", desc: "mapStreetsDesc" },
};
if (MML_API_KEY.trim()) {
  LAYERS.mml = {
    url: "https://avoin-karttakuva.maanmittauslaitos.fi/avoin/wmts/1.0.0/maastokartta/default/WGS84_Pseudo-Mercator/{z}/{y}/{x}.png?api-key=" + encodeURIComponent(MML_API_KEY.trim()),
    max: 19, native: 18, attr: "© Maanmittauslaitos (CC BY 4.0)", name: "mapMml", desc: "mapMmlDesc",
  };
}
export const LAYER_ORDER = (["terrain", "mml", "streets"] as const).filter((n) => LAYERS[n]);

const savedLayer = lsGet("layer", "") as LayerName;
/** The chosen map style, used by every map in the app. */
export let layerName: LayerName = LAYERS[savedLayer] ? savedLayer : "terrain";
export function setLayerName(name: LayerName): void {
  layerName = name;
  lsSet("layer", name);
}

export function tileLayer(name: LayerName): L.TileLayer {
  const b = LAYERS[name] ?? LAYERS.terrain!;
  return L.tileLayer(b.url, { maxZoom: b.max, maxNativeZoom: b.native ?? b.max, attribution: b.attr, crossOrigin: true });
}

// Water depths drawn over the base map: Traficom nautical charts (sea and the big charted lakes)
// and Syke lake soundings (also small lakes). Both are open Finnish government data.
export let depthOn = lsGet("depth", "1") === "1";
export function setDepthOn(on: boolean): void {
  depthOn = on;
  lsSet("depth", on ? "1" : "0");
}
const DEPTH_MONTH = "10/2026";
export function depthLayers(): L.LayerGroup<L.TileLayer.WMS> {
  const wms: L.WMSOptions = { format: "image/png", transparent: true, version: "1.3.0", maxZoom: 19, opacity: 0.8, tileSize: 512, crossOrigin: true };
  return L.layerGroup([
    // only when zoomed in enough to read them; zoomed out they cover the whole country in dots and lines
    L.tileLayer.wms("https://julkinen.traficom.fi/inspirepalvelu/rajoitettu/wms", { ...wms, minZoom: 9, layers: "DepthArea_A,DepthContour_L,Sounding_P",
      attribution: `Depths: Traficom ${DEPTH_MONTH}, not for navigation` }),
    L.tileLayer.wms("https://paikkatiedot.ymparisto.fi/geoserver/inspire_el/wms", { ...wms, minZoom: 10, layers: "EL.Syvyysalue,EL.ContourLine,EL.SpotElevation",
      attribution: "Syke (CC BY 4.0)" }),
  ]);
}
/** Adds the depth overlay to a map when it's switched on. */
export function withDepth(map: L.Map): L.Map {
  if (depthOn && hasLeaflet()) depthLayers().addTo(map);
  return map;
}

/** Where the newest catch with a place was, to start maps there. */
export function lastLoc(): LatLon | null {
  const c = catches.filter((x) => x.loc).sort((a, b) => b.time - a.time)[0];
  return c?.loc ? [c.loc.lat, c.loc.lon] : null;
}

/** Round catch marker: the photo when there is one, otherwise a fish or game icon. */
export function pinIcon(c: Catch, photoUrl: string | null): L.DivIcon {
  const d = el("div", { class: "pin" + (c.kind === "game" ? " game" : "") });
  if (photoUrl) d.style.backgroundImage = `url("${photoUrl}")`; else d.append(icon(c.kind));
  return L.divIcon({ html: d, className: "", iconSize: [40, 40], iconAnchor: [20, 20], popupAnchor: [0, -20] });
}

/** A Leaflet map with a function telling where to centre it after it is resized (see expand.ts). */
export type CentredMap = L.Map & { centre?: () => L.LatLngExpression };
